import React, { useState } from 'react'
import { useFetch } from 'use-http'

import { Box, Card, Heading, Stack, Text, Toggle } from '@myxplor/stardust'

import sortObjectsBy from '~/src/utils/sortObjectsBy'

import LoadingLoop from '~/src/components/LoadingLoop'

import { ServiceDetail } from './types'

interface Props {
  service: ServiceDetail
}

const Features = ({ service }: Props) => {
  const [featureStates, setFeatureStates] = useState<Record<string, boolean>>({})

  const featureRequest = useFetch(`/api/v1/features`, {}, [])
  const serviceToggles = useFetch(`/api/v1/services/${service.id}/toggles`, {})

  const features = sortObjectsBy(featureRequest.data?.data || [], 'name') as Papi.Feature[]
  const featureNames = Object.keys(service.features)

  const onToggleFeature = async (feature: Papi.Feature, enabled: boolean) => {
    setFeatureStates((state) => ({ ...state, [feature.code]: enabled }))

    await serviceToggles.post({ feature: feature.code, enabled })
  }

  if (featureRequest.loading) {
    return <LoadingLoop />
  }

  if (featureRequest.error) {
    return <div>Error...</div>
  }

  return (
    <Card>
      <Box display="flex" flexDirection="column">
        <Box alignItems="center" display="flex" justifyContent="space-between" padding="small">
          <Heading level={5}>Features</Heading>
        </Box>
        {featureNames.length === 0 && (
          <Box borderColor="lowestEmphasis" border="1px solid" padding="small">
            <Text>This service has no features</Text>
          </Box>
        )}
        {features.map((feature) => {
          const featureStatus =
            featureStates[feature.code] == null ? service.features[feature.code] : featureStates[feature.code]

          if (featureStatus === undefined) {
            return null
          }

          return (
            <Box
              key={feature.id}
              alignItems="center"
              borderTop="1px solid"
              borderColor="lowerEmphasis"
              display="flex"
              justifyContent="space-between"
              padding="small"
              width={1}>
              <Box paddingRight="large">
                <Stack space="xxxsmall">
                  <Text weight="bold">{feature.name}</Text>
                  {feature.description && <Text lineHeight={1.3}>{feature.description}</Text>}
                </Stack>
              </Box>
              <Box>
                <Toggle on={featureStatus} onChange={() => onToggleFeature(feature, !featureStatus)} />
              </Box>
            </Box>
          )
        })}
      </Box>
    </Card>
  )
}

export default React.memo(Features)
